import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import { ClientProvider } from './context/ClientContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import TopBar from './components/TopBar';
import PortfolioPlaybookTour from './components/PortfolioPlaybookTour';
import ProtectedRoute from './components/ProtectedRoute';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import CreateMealPlanPage from './pages/CreateMealPlanPage';
import ClientMealPlansPage from './pages/ClientMealPlansPage';
import ClientFoodLogsPage from './pages/ClientFoodLogsPage';
import ChatPage from './pages/ChatPage';
import AnalyticsPage from './pages/AnalyticsPage';
import NotificationsPage from './pages/NotificationsPage';
import ContactSupportPage from './pages/ContactSupportPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import EmployeeManagementPage from './pages/EmployeeManagementPage';
import { fetchDashboardClients, fetchAllClients, getTopbarClientsCache, setTopbarClientsCache } from './services/clientApi';
import NotFoundPage from './pages/NotFoundPage';
import './App.css';
import SignupPage from './pages/SignupPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AuthConfirmPage from './pages/AuthConfirmPage';
import TrainingManagement from './pages/TrainingManagement';
import TrainingManagementBuilder from './pages/TrainingManagement/PlanBuilder';
import LandingPageConfigBuilder from './pages/LandingPageConfigBuilder';
import ManagerDashboardPage from './pages/ManagerDashboardPage';
import MyLinkPage from './pages/MyLinkPage';
import TeamPage from './pages/TeamPage';
import { Toaster } from 'sonner';

// When logged in, "/" redirects to admin dashboard (first page after login)
const HomeOrRedirect = () => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-canvas dark:bg-canvas-dark" role="status" aria-label="Loading">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-line dark:border-line-dark border-t-forest-ondark dark:border-t-forest motion-reduce:animate-none"></div>
      </div>
    );
  }
  if (isAuthenticated) return <Navigate to="/dietitian-dashboard" replace />;
  return <LoginPage />;
};

// Component to conditionally render TopBar
const AppContent = () => {
  const location = useLocation();
  const isPublicAuthPage = location.pathname === '/' || location.pathname === '/signup' || location.pathname === '/forgot-password' || location.pathname === '/reset-password' || location.pathname === '/auth/confirm' || location.pathname === '/contact-support';
  const isFullHeightShell = location.pathname === '/landing-page-builder';
  const isAdminDashboard = location.pathname === '/dietitian-dashboard';
  const [clients, setClients] = useState([]);
  const [isLoadingClients, setIsLoadingClients] = useState(true);

  // TopBar clients: lightweight list when on client dashboard (fast); full dashboard data only when on admin page.

  useEffect(() => {
    if (!isPublicAuthPage) {
      const cached = getTopbarClientsCache();
      if (cached.length > 0 && !isAdminDashboard) {
        setClients(cached);
        setIsLoadingClients(false);
        return;
      }
      if (cached.length > 0 && isAdminDashboard) {
        setClients(cached);
      }
      const loadClients = async () => {
        try {
          setIsLoadingClients(true);
          // Full dashboard (engagement, KPIs) only on admin page; elsewhere use lightweight client list
          const clientsData = isAdminDashboard
            ? await fetchDashboardClients()
            : await fetchAllClients();
          const list = Array.isArray(clientsData) ? clientsData : (clientsData?.clients ? clientsData.clients : []);
          setTopbarClientsCache(list);
          setClients(list);
        } catch (error) {
          console.error('Error loading clients:', error);
          setClients([]);
        } finally {
          setIsLoadingClients(false);
        }
      };
      loadClients();
    }
  }, [isPublicAuthPage, isAdminDashboard]);

  // When admin dashboard Refresh is clicked, it updates the cache and fires this event.
  useEffect(() => {
    if (isPublicAuthPage) return;
    const onTopbarClientsUpdated = (e) => {
      setClients(e.detail || []);
    };
    window.addEventListener('topbar-clients-updated', onTopbarClientsUpdated);
    return () => window.removeEventListener('topbar-clients-updated', onTopbarClientsUpdated);
  }, [isPublicAuthPage]);

  useEffect(() => {
    if (!isFullHeightShell) return undefined;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    return () => {
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
    };
  }, [isFullHeightShell]);

  const toastSide = document.documentElement.dir === 'rtl' ? 'top-left' : 'top-right';

  return (
    <>
      <Toaster richColors position={toastSide} />
      {/* TopBar appears on all pages except Login and Signup */}
      {!isPublicAuthPage && <TopBar clients={clients} isLoading={isLoadingClients} />}

      {/* Guided tour — multi-page portfolio playbook */}
      {!isPublicAuthPage && <PortfolioPlaybookTour />}

      <div
        className={
          !isPublicAuthPage
            ? isFullHeightShell
              ? 'fixed inset-x-0 bottom-0 top-14 sm:top-16 xl:top-[4.5rem] z-0 overflow-hidden'
              : 'pt-14 sm:pt-16 xl:pt-[4.5rem]'
            : ''
        }
      >
        <Routes>
          <Route path="/" element={<HomeOrRedirect />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/auth/confirm" element={<AuthConfirmPage />} />
          <Route path="/contact-support" element={<ContactSupportPage />} />
          <Route path="/client-dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/dietitian-dashboard" element={<ProtectedRoute><AdminDashboardPage /></ProtectedRoute>} />
          <Route path="/overview" element={<Navigate to="/dietitian-dashboard" replace />} />
          <Route path="/employee-management" element={<ProtectedRoute><EmployeeManagementPage /></ProtectedRoute>} />
          <Route path="/create-meal-plan" element={<ProtectedRoute><CreateMealPlanPage /></ProtectedRoute>} />
          <Route path="/client-meal-plans" element={<ProtectedRoute><ClientMealPlansPage /></ProtectedRoute>} />
          <Route path="/client-food-logs" element={<ProtectedRoute><ClientFoodLogsPage /></ProtectedRoute>} />
          <Route path="/landing-page-builder" element={<ProtectedRoute><LandingPageConfigBuilder /></ProtectedRoute>} />
          <Route path="/manager-dashboard" element={<ProtectedRoute><ManagerDashboardPage /></ProtectedRoute>} />
          <Route path="/my-link" element={<ProtectedRoute><MyLinkPage /></ProtectedRoute>} />
          <Route path="/team" element={<ProtectedRoute><TeamPage /></ProtectedRoute>} />
          <Route path="/chat" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
          <Route path="/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
          <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
          <Route path="/training-management" element={<ProtectedRoute><TrainingManagement /></ProtectedRoute>} />
          <Route path="/training-management/builder/:planId?" element={<ProtectedRoute><TrainingManagementBuilder /></ProtectedRoute>} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </div>
    </>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <ClientProvider>
          <Router>
            <AppContent />
          </Router>
        </ClientProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;